import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from 'lucide-react';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

import { useLanguage } from '@/contexts/LanguageContext';

/** Follows ThemeContext, which toggles `.dark` on <html> for the dark themes. */
function useDocumentDark() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const html = document.documentElement;
    const observer = new MutationObserver(() => setDark(html.classList.contains('dark')));
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
    setDark(html.classList.contains('dark'));
    return () => observer.disconnect();
  }, []);
  return dark;
}

const tone = (color: string) => ({
  bg: `color-mix(in srgb, var(${color}) 10%, var(--lm-surface))`,
  border: `color-mix(in srgb, var(${color}) 30%, var(--lm-surface))`,
  text: `var(${color})`,
});
const ok = tone('--lm-ok');
const info = tone('--lm-label');
const warn = tone('--lm-warn');
const err = tone('--lm-err');

/**
 * Lumen toaster. Pass `theme` to force sonner's mode (e.g. inside a `.lm-dark` subtree); it follows
 * the app mode otherwise. The colours come from the Lumen tokens either way.
 *
 * z-index 10001 is the toast tier, shared with Mantine Notifications (App.tsx): above every overlay
 * (Mantine 200–400, Lumen 500) so a toast fired from a dialog is never under its scrim. The two never
 * overlap, sonner sits bottom-right and Mantine top-right. pointer-events stay on while a Radix modal
 * sets `pointer-events: none` on <body>, and Lumen layers do not treat a toast click as outside.
 */
function Toaster({ theme, style, ...props }: ToasterProps) {
  const dark = useDocumentDark();
  const { t } = useLanguage();

  return (
    <Sonner
      theme={theme ?? (dark ? 'dark' : 'light')}
      containerAriaLabel={t('lumen.toast.region')}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4 text-ok" />,
        info: <InfoIcon className="size-4 text-label" />,
        warning: <TriangleAlertIcon className="size-4 text-warn" />,
        error: <OctagonXIcon className="size-4 text-err" />,
        loading: <Loader2Icon className="size-4 animate-spin text-fg-muted" />,
      }}
      toastOptions={{
        classNames: {
          toast: 'shadow-overlay!',
          description: 'text-fg-2!',
        },
      }}
      style={
        {
          '--normal-bg': 'var(--lm-surface)',
          '--normal-text': 'var(--lm-fg)',
          '--normal-border': 'var(--lm-line-strong)',
          '--border-radius': '8px',
          // Only read with `richColors`; default toasts colour just the icon.
          '--success-bg': ok.bg,
          '--success-border': ok.border,
          '--success-text': ok.text,
          '--info-bg': info.bg,
          '--info-border': info.border,
          '--info-text': info.text,
          '--warning-bg': warn.bg,
          '--warning-border': warn.border,
          '--warning-text': warn.text,
          '--error-bg': err.bg,
          '--error-border': err.border,
          '--error-text': err.text,
          zIndex: 10001,
          pointerEvents: 'auto',
          ...style,
        } as React.CSSProperties
      }
      {...props}
    />
  );
}

export { Toaster };
